import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "../../interfaces/DecisionPriority";
import { MCTSUtils_v2 } from "../../../utils/MCTSUtils";
import { MCTSMoveGenerationService } from "../../../utils/MCTSMoveGenerationService";
import { MonkeyHandler } from "../03_abstract-priorities/Handlers/Monkeyhandler";

/**
 * Priority 1: Race Victory
 *
 * HIGHEST PRIORITY: If we (Currently testing as Dark) can win the race immediately (Dark King to row 0), take it!
 * This should trigger immediate early exit from MCTS with the winning move.
 * This needs a simple single ply check to see if it can simulate the Dark king moving on the next turn to row 0.
 * Only Special case - the AI king is a Monkey and its only win path is an Enemy Bear capture (66% chance of death!)
 */
export class RaceVictoryPriority extends BaseDecisionPriority {
  readonly priority = 1;
  readonly name = "Race Victory";
  readonly description = "HIGHEST: Take immediate race victories";

  isRelevant(context: DecisionContext): boolean {
    // Always relevant - we should always check for immediate wins
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.log("🏆 Analyzing for immediate race victory...", context);

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;

    // DEBUG: Log the current player and game state

    // Determine our victory row (Light wins on row 7, Dark wins on row 0)
    const ourVictoryRow = isPlayerTurn ? 7 : 0;

    // CRITICAL FIX: Generate fresh moves to bypass any potential filtering
    // Race victory must ALWAYS analyze the full move set!
    const moveService = MCTSMoveGenerationService.getInstance();
    const originalMoves = moveService.getAllMoves(gameState);

    // Find all KING moves that reach our victory row
    const raceWinMoves = originalMoves.filter((moveInfo) => {
      // Only KING pieces can win by racing
      const piece = MCTSUtils_v2.getPieceAt(
        gameState.board,
        moveInfo.fromPosition
      );
      const isKingMove =
        piece && piece.isKing && moveInfo.move.position.row === ourVictoryRow;

      if (isKingMove) {
      }

      return isKingMove;
    });


    if (raceWinMoves.length === 0) {
      this.log("No immediate race victory moves found", context);
      return this.createNoActionResult("No immediate race victory available");
    }

    // Get the king piece information
    const piece = MCTSUtils_v2.getPieceAt(
      gameState.board,
      raceWinMoves[0].fromPosition
    );
    const pieceType = piece?.type || "Unknown";

    let selectedMove = raceWinMoves[0];

    // MONKEY SPECIAL CASE: Simple safety logic without MonkeyHandler to avoid recursion

    if (piece?.type === "Monkey") {

      // Classify moves by safety: Empty squares > Non-bear captures > Bear captures
      const emptySquareMoves = raceWinMoves.filter((moveInfo) => {
        const targetPiece = MCTSUtils_v2.getPieceAt(
          gameState.board,
          moveInfo.move.position
        );
        const isEmpty = !targetPiece;
        return isEmpty;
      });

      const nonBearCaptures = raceWinMoves.filter((moveInfo) => {
        const targetPiece = MCTSUtils_v2.getPieceAt(
          gameState.board,
          moveInfo.move.position
        );
        return targetPiece && targetPiece.type !== "Bear";
      });

      const bearCaptures = raceWinMoves.filter((moveInfo) => {
        const targetPiece = MCTSUtils_v2.getPieceAt(
          gameState.board,
          moveInfo.move.position
        );
        return targetPiece && targetPiece.type === "Bear";
      });


      // PRIORITY ORDER: Empty squares > Non-bear captures > Bear captures
      if (emptySquareMoves.length > 0) {
        selectedMove = emptySquareMoves[0];
      } else if (nonBearCaptures.length > 0) {
        selectedMove = nonBearCaptures[0];
      } else if (bearCaptures.length > 0) {
        selectedMove = bearCaptures[0];
      }
    } else {
      // Non-monkey pieces: just take first race move
    }

    this.log(
      `🏆 RACE VICTORY: ${pieceType} ${this.formatPosition(
        selectedMove.fromPosition
      )} reaches victory row ${ourVictoryRow} at ${this.formatPosition(
        selectedMove.move.position
      )}`,
      context
    );

    return this.createEarlyExitResult(
      selectedMove,
      `INSTANT WIN - ${pieceType} reaches victory row ${ourVictoryRow}`,
      {
        victoryType: "race",
        victoryRow: ourVictoryRow,
        totalWinningMoves: raceWinMoves.length,
      }
    );
  }
}

